import { supabase } from '@/integrations/supabase/client';

export interface BugAnalysis {
  id: string;
  bug_report_id: string;
  status: 'analyzing' | 'questions_pending' | 'ready' | 'pr_created' | 'error';
  analysis_summary: string | null;
  fix_plan: string | null;
  github_files_analyzed: string[] | null;
  pr_url: string | null;
  pr_number: number | null;
  error_message: string | null;
}

export interface BugAIQuestion {
  id: string;
  analysis_id: string;
  question_text: string;
  options: string[];
  answer: string | null;
  answered_at: string | null;
  question_order: number;
}

export async function triggerBugAnalysis(bugReportId: string): Promise<{
  action: 'questions' | 'ready' | 'redirected_to_feature';
  analysisId?: string;
  questions?: BugAIQuestion[];
  summary?: string;
  fixPlan?: string;
  relevantFiles?: string[];
  featureRequestId?: string;
  message?: string;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke('analyze-bug-report', {
    body: { bugReportId },
  });

  if (error) throw new Error(error.message || 'Analysis failed');
  if (data == null) throw new Error('No response');
  return typeof data === 'string' ? JSON.parse(data) : data;
}

export async function answerAndContinueAnalysis(
  bugReportId: string,
  questionId: string,
  answer: string
): Promise<{
  action: 'questions' | 'ready';
  analysisId: string;
  questions?: BugAIQuestion[];
  summary?: string;
  fixPlan?: string;
  relevantFiles?: string[];
}> {
  const { data, error } = await supabase.functions.invoke('analyze-bug-report', {
    body: { bugReportId, answerQuestionId: questionId, answer },
  });

  if (error) throw new Error(error.message || 'Analysis failed');
  if (data == null) throw new Error('No response');
  return typeof data === 'string' ? JSON.parse(data) : data;
}

export async function getAnalysisForBugReport(bugReportId: string): Promise<BugAnalysis | null> {
  const { data, error } = await supabase
    .from('bug_report_analyses' as any)
    .select('*')
    .eq('bug_report_id', bugReportId)
    .single();

  if (error) return null;
  return data as unknown as BugAnalysis;
}

export async function getQuestionsForAnalysis(analysisId: string): Promise<BugAIQuestion[]> {
  const { data, error } = await supabase
    .from('bug_report_ai_questions' as any)
    .select('*')
    .eq('analysis_id', analysisId)
    .order('question_order', { ascending: true });

  if (error) return [];
  return (data || []) as unknown as BugAIQuestion[];
}

export async function createBugFixPR(analysisId: string): Promise<{
  success: boolean;
  accepted?: boolean;
  prUrl?: string;
  prNumber?: number;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke('create-bug-fix-pr', {
    body: { analysisId },
  });

  if (error) throw new Error(error.message || 'PR creation failed');
  if (data?.accepted === true) return { success: true, accepted: true };
  return data;
}
